import React, { useState } from 'react';
import { Alert, Button, Card, Col, Drawer, Form, Modal, Row } from 'antd';
import { Filter } from 'components';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import {
  createFileExport,
  openTransfer,
  refresh
} from 'stores/financialEntries/financialEntryActions';
import { configManager, api } from '@unicus/react-ui-kit';
import { useHistory } from 'react-router';
import { PAYMENT, RECEIPT } from 'constants/types';
import qs from 'query-string';
import { PrintContextConsumer } from 'react-to-print';
import FinancialEntryFilter from './FinancialEntryFilter';
import FinancialEntrySummary from './FinancialEntrySummary';
import FinancialEntryFilterAdvanced from './FinancialEntryFilterAdvanced';

const FinancialEntryHeader = (props) => {
  const {
    data,
    disableEditing,
    customButtons,
    initCreation,
    queryParams,
    setQueryParams,
    mode = 'all',
    initialValuesOfCreation = {},
    hideSummary,
    fixedQueryParams = {},
    defaultQueryParams = {},
    isBankReconciliation
  } = props;

  const dispatch = useDispatch();
  const [filterAdvanced, setFilterAdvanced] = useState(false);
  const [form] = Form.useForm();
  const [formDrawer] = Form.useForm();
  const history = useHistory();
  const { exportingExcel } = useSelector(
    (c) => c.resources['financial-entries'] ?? {}
  );

  const allowCreateOrUpdate = () => {
    if (disableEditing) {
      return false;
    }

    return true;
  };

  const allowSearch = () => {
    return history.location?.pathname !== '/app/recipient-care/profiles';
  };

  const handleClickPayment = () => {
    initCreation({ type: 'payment', ...initialValuesOfCreation });
  };

  const handleClickReceipt = () => {
    initCreation({ type: 'receipt', ...initialValuesOfCreation });
  };

  const handleRefreshPage = () => {
    dispatch(refresh());
  };

  const convertParamsOfArray = (param) => {
    if (Array.isArray(param)) {
      return param;
    }

    if (!param) {
      return [];
    }

    return [param];
  };

  const handleChangeFilter = ({
    rangePicker,
    categories = [],
    checkingAccounts = [],
    costCenters = [],
    paymentMethods = [],
    serviceProviderIds = [],
    dateType = queryParams.dateType,
    ...rest
  }) => {
    const params = {
      dateType,
      initialDate: rangePicker
        ? moment(rangePicker[0]).format('YYYY/MM/DD')
        : null,

      endDate: rangePicker ? moment(rangePicker[1]).format('YYYY/MM/DD') : null,
      categoryIds: convertParamsOfArray(categories).map((c) => c.id),
      checkingAccountIds: convertParamsOfArray(checkingAccounts).map(
        (c) => c.key
      ),
      costCenterIds: convertParamsOfArray(costCenters).map((c) => c.key),
      paymentMethods,
      serviceProviderIds: serviceProviderIds.map((c) => c.key),
      ...fixedQueryParams,
      ...rest
    };

    setQueryParams(params);
  };

  const normalizeParams = (param, key = 'id') => {
    if (Array.isArray(param)) {
      return param.map((c) => ({ [key]: c }));
    }

    if (!Array.isArray(param)) {
      return [{ [key]: param }];
    }

    return null;
  };

  const handleBeforeBinding = ({
    initialDate,
    endDate,
    categoryIds = [],
    checkingAccountIds = [],
    costCenterIds = [],
    paymentMethods = [],
    serviceProviderIds = [],
    ...rest
  }) => {
    rest.rangePicker = [moment(initialDate), moment(endDate)];

    rest.categories = normalizeParams(categoryIds);
    rest.checkingAccounts = normalizeParams(checkingAccountIds, 'key');
    rest.costCenters = normalizeParams(costCenterIds, 'key');
    rest.serviceProviderIds = normalizeParams(serviceProviderIds, 'key');
    rest.paymentMethods = paymentMethods;

    return {
      ...rest
    };
  };

  const handleOpenFilterAdvanced = () => {
    setFilterAdvanced(true);

    formDrawer.resetFields();
    formDrawer.setFieldsValue(handleBeforeBinding(queryParams));
  };

  const handleCloseFilterAdvanced = () => {
    setFilterAdvanced(false);
  };

  const handleApplyDrawerFilter = () => {
    formDrawer.validateFields().then((values) => {
      handleChangeFilter(values);
    });

    handleCloseFilterAdvanced();
  };

  const handleClearFilter = () => {
    handleChangeFilter(defaultQueryParams);
    handleCloseFilterAdvanced();
  };

  const handleChangeFilterSummary = (otherValues) => {
    form.validateFields().then((values) => {
      handleChangeFilter({ ...values, ...otherValues });
    });
  };

  const handleFileExportOnSuccess = ({ data }) => {
    const { fileName } = data.result;
    const url = api.getUrlFromResource(
      `financial-entries/file-export/${fileName}`
    );
    window.open(url, '_blank');
  };

  const handleExportExcel = () => {
    Modal.confirm({
      title: 'Deseja exportar para Excel?',
      okText: 'Exportar',
      onOk: () => {
        const querySearch = qs.stringify(
          { ...queryParams, ...fixedQueryParams },
          {
            arrayFormat: 'repeat',
            skipNull: true
          }
        );

        dispatch(createFileExport(querySearch, handleFileExportOnSuccess));
      }
    });
  };

  const handleOpenTransfer = () => {
    dispatch(openTransfer());
  };

  return (
    <PrintContextConsumer>
      {({ handlePrint }) => (
        <div className="gx-financialentry-header">
          {!hideSummary && (
            <FinancialEntrySummary onFilter={handleChangeFilterSummary} />
          )}
          <Card className="gx-card gx-mb-0" bodyStyle={{ padding: 12 }}>
            <Row className="no-padding no-margin" gutter={[0, 10]}>
              <Col flex={1}>
                <Filter
                  onChange={handleChangeFilter}
                  queryParams={queryParams}
                  onBeforeBinding={handleBeforeBinding}
                  form={form}
                >
                  <FinancialEntryFilter
                    allowSearch={allowSearch()}
                    onRefreshPage={handleRefreshPage}
                    onFilterAdvanced={handleOpenFilterAdvanced}
                    onClearFilter={handleClearFilter}
                    onExportExcel={handleExportExcel}
                    exportingExcel={exportingExcel}
                    loading={data.loading}
                    onPrint={handlePrint}
                  />
                </Filter>
                {filterAdvanced ? (
                  <Drawer
                    className="gx-drawer-form sm"
                    title={
                      isBankReconciliation
                        ? 'Filtros (Conciliação bancária)'
                        : 'Filtros'
                    }
                    visible
                    onClose={handleCloseFilterAdvanced}
                    bodyStyle={{ paddingBottom: 80 }}
                  >
                    <Form
                      form={formDrawer}
                      initialValues={handleBeforeBinding(queryParams)}
                      {...configManager.getConfig().layout.form}
                    >
                      <FinancialEntryFilterAdvanced mode={mode} />
                      <div
                        style={{
                          position: 'absolute',
                          right: 0,
                          bottom: 0,
                          width: '100%',
                          borderTop: '1px solid #e9e9e9',
                          padding: '10px 16px',
                          background: '#fff',
                          textAlign: 'right'
                        }}
                      >
                        <div>
                          <Button type="link" onClick={handleClearFilter}>
                            Limpar filtro
                          </Button>
                          <Button onClick={handleCloseFilterAdvanced}>
                            Voltar
                          </Button>
                          <Button
                            type="primary"
                            onClick={handleApplyDrawerFilter}
                          >
                            Aplicar filtro
                          </Button>
                        </div>
                      </div>
                    </Form>
                  </Drawer>
                ) : null}
              </Col>
              {allowCreateOrUpdate() && (
                <Col>
                  <Button onClick={handleOpenTransfer}>Transferência</Button>
                  {['all', PAYMENT].includes(mode) && (
                    <Button className="payment" onClick={handleClickPayment}>
                      Despesa
                    </Button>
                  )}
                  {['all', RECEIPT].includes(mode) && (
                    <Button className="receipt" onClick={handleClickReceipt}>
                      Receita
                    </Button>
                  )}
                </Col>
              )}
              {customButtons && <Col>{customButtons()}</Col>}
            </Row>
          </Card>
        </div>
      )}
    </PrintContextConsumer>
  );
};

export default FinancialEntryHeader;
